import { FieldError } from './ui';
import { translation, LocaleCode, locales, LocalizedTexts } from './translation';

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 2500;

type ProductStatus = 'processing' | 'completed' | 'failed' | 'draft';

type ProductAvailability = 'available' | 'reserved' | 'sold';

const productAvailabilityList: ProductAvailability[] = ['available', 'reserved', 'sold'];

const isValidAvailability = (availability: string): boolean => {
	return productAvailabilityList.includes(availability as ProductAvailability);
};

type ProductOrderBy = 'popularity' | 'pricedesc' | 'priceasc' | 'datedesc' | 'dateasc';

type ProductImagePosition = number | null; // if position is null it's the main image, otherwise it's the n image of gallery.

interface ProductImage {
	id: number;
	fileName: string;
	originalExtension: string;
	position: ProductImagePosition;
	isCutout?: boolean; // only on main image.
}

interface ProductTexts {
	title: string;
	description: string;
	slug: string;
}

interface AttributeReference {
	id: number;
	name: string;
	slug: string;
}

interface AuthorReference {
	id: number;
	name: string;
	slug: string;
	isApproved: boolean;
}

interface ProductPublication {
	merchantIntegrationID: number;
	integrationKey: string;
	externalID: string | null;
}

interface Product {
	id: number;
	texts: Record<LocaleCode, ProductTexts>;
	price: number;
	condition: string;
	availability: ProductAvailability;
	visible: boolean;
	status: ProductStatus;
	processingError: string | null; // returned only in the admin.
	imageReprocessing: boolean;
	isFavorite: boolean;
	image: ProductImage;
	originalImage?: ProductImage; // returned only in the admin.
	gallery: ProductImage[];
	width: number | null;
	length: number | null;
	depth: number | null;
	height: number | null;
	diameter: number | null;
	sku: string | null;
	shippingZoneA: number | null;
	shippingZoneB: number | null;
	shippingZoneC: number | null;
	shippingZoneD: number | null;
	shippingZoneE: number | null;
	shippingZoneAOnRequest: boolean;
	shippingZoneBOnRequest: boolean;
	shippingZoneCOnRequest: boolean;
	shippingZoneDOnRequest: boolean;
	shippingZoneEOnRequest: boolean;
	merchant: AttributeReference;
	category: AttributeReference;
	century: AttributeReference;
	style: AttributeReference;
	author: AuthorReference | null;
	origin: AttributeReference | null;
	materials: string[] | null;
	views?: number; // returned only in the admin.
	favoritesCount?: number; // returned only in the admin.
	publications?: ProductPublication[]; // returned only in the admin.
	createdAt: string;
	updatedAt: string;
	deletedAt: string | null;
}

interface ProductToSet {
	title: string | LocalizedTexts;
	description: string | LocalizedTexts;
	price: number;
	conditionID: number;
	availability: ProductAvailability;
	visible: boolean;
	width: number | null;
	length: number | null;
	depth: number | null;
	height: number | null;
	diameter: number | null;
	sku: string | null;
	shippingZoneA: number | null;
	shippingZoneB: number | null;
	shippingZoneC: number | null;
	shippingZoneD: number | null;
	shippingZoneE: number | null;
	shippingZoneAOnRequest: boolean;
	shippingZoneBOnRequest: boolean;
	shippingZoneCOnRequest: boolean;
	shippingZoneDOnRequest: boolean;
	shippingZoneEOnRequest: boolean;
	categoryID: number;
	centuryID: number;
	styleID: number;
	authorID: number | null;
	originID: number | null;
	proposedAuthor: string | null;
	materialsIDs: number[] | null;
}

const validateProductToSet = (p: ProductToSet, locale: LocaleCode, isUpdate: boolean) => {
	const t = translation[locale];

	if (isUpdate) {
		validateProductTextUpdate(
			p.title as LocalizedTexts,
			p.description as LocalizedTexts,
			locale,
		);
	} else {
		// title.
		const ti = p.title as string | undefined;
		if (ti == null) {
			throw new FieldError(t['Title is required'], 'title');
		}
		if ([...ti].length > MAX_TITLE_LENGTH) {
			throw new FieldError(t['Title must be 100 characters or less'], 'title');
		}

		// description.
		const l = p.description as string | undefined;
		if (l == null) {
			throw new FieldError(t['Description is required'], 'description');
		}
		if ([...l].length > MAX_DESCRIPTION_LENGTH) {
			throw new FieldError(t['Description must be 2500 characters or less'], 'description');
		}
	}

	// price.
	if (p.price == null) {
		throw new FieldError(t['Price is required'], 'price');
	}
	if (p.price <= 0) {
		throw new FieldError(t['Price must be greater than zero'], 'price');
	}
	if (!Number.isInteger(p.price)) {
		throw new FieldError(t['Price must be a whole number'], 'price');
	}

	// condition.
	if (p.conditionID == null) {
		throw new FieldError(t['Condition is required'], 'condition');
	}

	// category.
	if (p.categoryID == null) {
		throw new FieldError(t['Category is required'], 'category');
	}

	// century.
	if (p.centuryID == null) {
		throw new FieldError(t['Epoch is required'], 'century');
	}

	// style.
	if (p.styleID == null) {
		throw new FieldError(t['Style is required'], 'style');
	}

	// dimensions.
	if (p.width != null && p.width <= 0) {
		throw new FieldError(t['Width must be greater than zero'], 'width');
	}

	if (p.length != null && p.length <= 0) {
		throw new FieldError(t['Length must be greater than zero'], 'length');
	}

	if (p.depth != null && p.depth <= 0) {
		throw new FieldError(t['Depth must be greater than zero'], 'depth');
	}

	if (p.height != null && p.height <= 0) {
		throw new FieldError(t['Height must be greater than zero'], 'height');
	}

	if (p.diameter != null && p.diameter <= 0) {
		throw new FieldError(t['Diameter must be greater than zero'], 'diameter');
	}

	// shipping zones.
	const shippingZones = [
		{ value: p.shippingZoneA, onRequest: p.shippingZoneAOnRequest, field: 'shipping-zone-A' },
		{ value: p.shippingZoneB, onRequest: p.shippingZoneBOnRequest, field: 'shipping-zone-B' },
		{ value: p.shippingZoneC, onRequest: p.shippingZoneCOnRequest, field: 'shipping-zone-C' },
		{ value: p.shippingZoneD, onRequest: p.shippingZoneDOnRequest, field: 'shipping-zone-D' },
		{ value: p.shippingZoneE, onRequest: p.shippingZoneEOnRequest, field: 'shipping-zone-E' },
	] as const;
	for (const zone of shippingZones) {
		if (zone.value != null && zone.value < 0) {
			throw new FieldError(t['Shipping zone cost cannot be negative'], zone.field);
		}
		if (zone.onRequest && zone.value != null) {
			throw new FieldError(
				t['Shipping zone cannot be both on request and have a price'],
				zone.field,
			);
		}
	}

	// materials.
	if (p.materialsIDs != null && p.materialsIDs.length > 3) {
		throw new FieldError(t['You can select at most 3 materials'], 'materials');
	}
};

// validateProductTextUpdate validates product texts during updates.
// Text length validation is intentionally skipped because product
// texts may already exceed the max length enforced at creation due to
// automatic translations.
const validateProductTextUpdate = (
	title: LocalizedTexts,
	description: LocalizedTexts,
	locale: LocaleCode,
) => {
	const t = translation[locale];

	for (const lang of locales) {
		const l = lang as LocaleCode;

		let language = '';
		if (l === 'it') {
			language = t['Italian'];
		} else if (l === 'en') {
			language = t['English'];
		} else if (l === 'fr') {
			language = t['French'];
		}
		language = language.toLowerCase();

		// title.
		const ti = title as LocalizedTexts;
		const tti = ti[l];
		if (tti == null) {
			throw new FieldError(
				t[`The field "Title" in the {lang} language is required`].replace(
					'{lang}',
					language,
				),
				'title',
			);
		}

		// description.
		const ld = description as LocalizedTexts;
		let ldt = ld[l];
		if (ldt == null) {
			throw new FieldError(
				t[`The field "Description" in the {lang} language is required`].replace(
					'{lang}',
					language,
				),
				'description',
			);
		}
	}
};

export {
	MAX_TITLE_LENGTH,
	MAX_DESCRIPTION_LENGTH,
	productAvailabilityList,
	validateProductToSet,
	validateProductTextUpdate,
	isValidAvailability,
};

export type {
	ProductImage,
	ProductTexts,
	ProductPublication,
	Product,
	ProductToSet,
	ProductStatus,
	ProductAvailability,
	ProductOrderBy,
	ProductImagePosition,
};
